/**
 * useTelegram — хук для Telegram WebApp SDK
 *
 * Предоставляет доступ к:
 * - tg.initData (для авторизации на сервере)
 * - tg.themeParams (цвета темы)
 * - BackButton, MainButton, HapticFeedback
 * - User info
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

// ─── Types ─────────────────────────────────────────────────

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
}

interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

interface WebApp {
  initData: string;
  initDataUnsafe: {
    user?: TelegramUser;
    start_param?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: ThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  ready: () => void;
  expand: () => void;
  close: () => void;
  BackButton: {
    isVisible: boolean;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  MainButton: {
    text: string;
    isVisible: boolean;
    isActive: boolean;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    setText: (text: string) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  showAlert: (message: string) => void;
  showConfirm: (message: string, callback: (confirmed: boolean) => void) => void;
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
  enableClosingConfirmation?: () => void;
  disableClosingConfirmation?: () => void;
  isClosingConfirmationEnabled?: boolean;
  onEvent: (eventType: string, eventHandler: () => void) => void;
  offEvent: (eventType: string, eventHandler: () => void) => void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: WebApp;
    };
  }
}

// ─── BackButton Dispatcher for React Router Lifecycle Resilience ────
let isDispatcherRegistered = false;
const backButtonCallbacks: Array<() => void> = [];

function backButtonDispatcher() {
  const lastCallback = backButtonCallbacks[backButtonCallbacks.length - 1];
  if (lastCallback) {
    lastCallback();
  }
}

// ─── Hook ──────────────────────────────────────────────────

export function useTelegram() {
  const [tg, setTg] = useState(() => window.Telegram?.WebApp);

  const user = useMemo((): TelegramUser | null => {
    if (tg?.initDataUnsafe?.user) return tg.initDataUnsafe.user;
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return {
        id: 1088744046,
        first_name: 'Разработчик',
        username: 'dev_user'
      };
    }
    return null;
  }, [tg]);

  const [colorScheme, setColorScheme] = useState<'light' | 'dark'>(tg?.colorScheme || 'light');

  const isDarkMode = useMemo(() => {
    return colorScheme === 'dark';
  }, [colorScheme]);

  const initData = useMemo(() => {
    let resolved = tg?.initData || '';

    if (!resolved && typeof window !== 'undefined') {
      try {
        const rawHash = window.location.hash.slice(1);
        const hashParams = new URLSearchParams(rawHash);
        const tgWebAppData = hashParams.get('tgWebAppData');
        if (tgWebAppData) {
          resolved = tgWebAppData;
        } else if (rawHash.startsWith('tgWebAppData=')) {
          const ampersandIdx = rawHash.indexOf('&');
          resolved = ampersandIdx !== -1 
            ? rawHash.slice('tgWebAppData='.length, ampersandIdx) 
            : rawHash.slice('tgWebAppData='.length);
          resolved = decodeURIComponent(resolved);
        }

        if (!resolved) {
          const searchParams = new URLSearchParams(window.location.search);
          const searchData = searchParams.get('tgWebAppData');
          if (searchData) resolved = searchData;
        }
      } catch {}
    }

    if (resolved) {
      try {
        sessionStorage.setItem('tg_init_data_backup', resolved);
      } catch {}
      return resolved;
    }

    try {
      const cachedBackup = sessionStorage.getItem('tg_init_data_backup');
      if (cachedBackup) return cachedBackup;
    } catch {}

    if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      const mockUser = {
        id: 1088744046,
        first_name: 'Разработчик',
        username: 'dev_user'
      };
      const authDate = Math.floor(Date.now() / 1000);
      return `user=${encodeURIComponent(JSON.stringify(mockUser))}&auth_date=${authDate}&hash=mock_hash`;
    }

    return '';
  }, [tg]);

  // Telegram ready + expand при монтировании + асинхронный поллинг для iOS WebKit
  useEffect(() => {
    if (tg) {
      try {
        tg.ready();
        tg.expand();
      } catch (e) {}
      return;
    }

    const interval = setInterval(() => {
      if (window.Telegram?.WebApp) {
        const webApp = window.Telegram.WebApp;
        setTg(webApp);
        try {
          webApp.ready();
          webApp.expand();
        } catch (e) {}
        clearInterval(interval);
      }
    }, 50);

    const timeout = setTimeout(() => clearInterval(interval), 3000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [tg]);

  // Подписка на themeChanged
  useEffect(() => {
    if (!tg) return;
    const handleThemeChange = () => {
      setColorScheme(tg.colorScheme || 'light');
    };
    tg.onEvent('themeChanged', handleThemeChange);
    return () => {
      tg.offEvent('themeChanged', handleThemeChange);
    };
  }, [tg]);

  const showBackButton = useCallback((onBack: () => void) => {
    if (!tg) return;
    
    backButtonCallbacks.push(onBack);
    if (!isDispatcherRegistered) {
      tg.BackButton.onClick(backButtonDispatcher);
      isDispatcherRegistered = true;
    }
    tg.BackButton.show();

    return () => {
      const idx = backButtonCallbacks.indexOf(onBack);
      if (idx > -1) {
        backButtonCallbacks.splice(idx, 1);
      }
      if (backButtonCallbacks.length === 0) {
        tg.BackButton.hide();
        tg.BackButton.offClick(backButtonDispatcher);
        isDispatcherRegistered = false;
      }
    };
  }, [tg]);

  const haptic = useMemo(() => ({
    impact: (style: 'light' | 'medium' | 'heavy' = 'light') => {
      tg?.HapticFeedback?.impactOccurred(style);
    },
    success: () => tg?.HapticFeedback?.notificationOccurred('success'),
    error: () => tg?.HapticFeedback?.notificationOccurred('error'),
    warning: () => tg?.HapticFeedback?.notificationOccurred('warning'),
    selection: () => tg?.HapticFeedback?.selectionChanged(),
  }), [tg]);

  return {
    tg,
    user,
    isDarkMode,
    initData,
    showBackButton,
    haptic,
    isInTelegram: !!tg,
  };
}
